import { ReactElement } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useTranslation } from 'react-i18next'

import { Box, HStack, Spacer, Text, Tooltip } from '@chakra-ui/react'

import { LabeledSwitch } from './LabeledSwitch'

interface DryRunToggleIProps {
  isDryRun: boolean
  onDryRunToggle: () => void
  isLoading?: boolean
  /** Externally forced read-only (e.g. rule viewer's view mode). */
  isDisabled?: boolean
}

export const DryRunToggle = ({
  isDryRun,
  onDryRunToggle,
  isLoading = false,
  isDisabled = false,
}: DryRunToggleIProps): ReactElement => {
  const { t } = useTranslation()
  const mode = useLightMode()
  const { isImplementer } = usePermissions()
  const isDisabledByPermissions = !isImplementer
  const isSwitchDisabled = isLoading || isDisabledByPermissions || isDisabled
  // The permission tooltip only makes sense when permissions are what is
  // blocking the toggle, not a read-only view mode.
  const shouldShowPermissionTooltip =
    isDisabledByPermissions && !isLoading && !isDisabled

  return (
    <Box>
      <HStack>
        <Box>
          <Text
            color={`text.primary.${mode}`}
            fontSize='16px'
            fontWeight='medium'
            letterSpacing='-0.8px'
            mb={2}
          >
            {t('automations:ai_dry_run_title')}
          </Text>
          <Text color={`text.secondary.${mode}`} fontSize='sm' mb={4}>
            {t('automations:ai_dry_run_description')}
          </Text>
        </Box>
        <Spacer />
        <Tooltip
          isDisabled={!shouldShowPermissionTooltip}
          label={t('automations:ai_dry_run_permissions_tooltip')}
          shouldWrapChildren
        >
          <Box>
            <LabeledSwitch
              id='dryRunToggle'
              isChecked={isDryRun}
              isDisabled={isSwitchDisabled}
              onSwitchChange={onDryRunToggle}
            />
          </Box>
        </Tooltip>
      </HStack>
    </Box>
  )
}
